import { useQueryClient } from '@tanstack/react-query'
import { Button, DatePicker, Flex, Form, Modal, ModalProps } from 'antd'
import dayjs from 'dayjs'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import {
  usePersonRiskGroupServiceEndPersonRiskGroupEndpoint,
  usePersonRiskGroupServiceGetPersonRiskGroupsEndpointKey
} from 'src/api/queries'
import { ApiError, ClassifierCategory, ClassifierType } from 'src/api/requests'
import { requiredRules } from 'src/app-constants'
import { ClassifierInfiniteSelect, message } from 'src/components'
import { useClassifierId } from 'src/hooks'
import { dayMonthYearFormatWithDot, yearMonthDaySlash } from 'src/libs'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

type Props = {
  onCancel: () => void
} & ModalProps

export const FinalizeRiskGroupModal = ({ onCancel, ...props }: Props) => {
  const { t } = useLocales()

  const [form] = Form.useForm()
  const { id } = useParams()

  const queryClient = useQueryClient()

  const exclusionReasonClassifierId = useClassifierId(
    ClassifierType.RISK_GROUP_EXCLUSION_REASON,
    ClassifierCategory.PERSONS
  )

  const { mutate } = usePersonRiskGroupServiceEndPersonRiskGroupEndpoint({
    onError: (error: ApiError) => formErrorHandler(error.body.errors),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [usePersonRiskGroupServiceGetPersonRiskGroupsEndpointKey]
      })
      message.success(t('risk_groups.success_finalize'))
      onCancel()
    }
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    mutate({
      personId: String(id),
      requestBody: {
        endDate: dayjs(values.endDate).format(yearMonthDaySlash),
        exclusionReasonId: values.exclusionReasonId
      }
    })
  }

  useEffect(() => {
    form.setFieldsValue({
      endDate: dayjs()
    })
  }, [])

  return (
    <Modal
      onCancel={onCancel}
      open
      maskClosable={false}
      footer={null}
      title={t('risk_groups.finalize')}
      {...props}
    >
      <Form form={form} layout="vertical" requiredMark={false}>
        <Flex vertical gap={24}>
          <Form.Item
            name="endDate"
            label={t('risk_groups.end_date')}
            rules={requiredRules}
          >
            <DatePicker
              className="full-width"
              format={dayMonthYearFormatWithDot}
            />
          </Form.Item>

          <Form.Item
            name="exclusionReasonId"
            label={t('risk_groups.exclusion_reason')}
            rules={requiredRules}
          >
            <ClassifierInfiniteSelect
              id={exclusionReasonClassifierId}
              placeholder={t('risk_groups.exclusion_reason')}
            />
          </Form.Item>

          <Flex justify="center" gap={16}>
            <Button onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="primary" onClick={onSubmit}>
              {t('common.save')}
            </Button>
          </Flex>
        </Flex>
      </Form>
    </Modal>
  )
}
